import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useShareBadgeMutation } from "../../../services/rtkApi/badgesApi";

/** The share page path for a share id; ignores anything the server sends that is not one. */
export function sharePath(res: { shareId?: string; path?: string } | null | undefined): string | null {
  if (!res) return null;
  if (typeof res.path === "string" && /^\/badge\/s\/[A-Za-z0-9_-]+$/.test(res.path)) return res.path;
  if (typeof res.shareId === "string" && /^[A-Za-z0-9_-]+$/.test(res.shareId)) return `/badge/s/${res.shareId}`;
  return null;
}

/**
 * The Share button: POST /api/badges/me/share (idempotent per member and badge),
 * then open the public share page. The reveal cards and the detail sheet use it.
 */
export function useShareBadge(): { share: (code: string) => Promise<boolean>; isLoading: boolean } {
  const [shareBadge, { isLoading }] = useShareBadgeMutation();
  const navigate = useNavigate();
  const share = useCallback(
    async (code: string) => {
      try {
        const res = await shareBadge({ code }).unwrap();
        const path = sharePath(res);
        if (!path) throw new Error("no share path");
        navigate(path);
        return true;
      } catch {
        toast.error("Could not make a share link. Try again in a minute.");
        return false;
      }
    },
    [shareBadge, navigate]
  );
  return { share, isLoading };
}
